import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY = { name: "", code: "", contact_person: "", phone: "", email: "", notes: "" };

/**
 * External Booking Platforms master (TVS Direct, Flipkart, Amazon, etc.)
 * Used by sales' payment-line dropdown when the customer pre-paid the vehicle
 * on an external platform. The dealership tracks the amount as a receivable
 * from the platform — settled in Accounts → Platform Receivables.
 */
export default function PlatformsPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // null | EMPTY | row
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platforms");
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const name = (editing.name || "").trim();
    if (!name) {
      toast.error("Platform name is required.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/platforms/${editing.id}`, { ...editing, name });
        toast.success("Platform updated");
      } else {
        await api.post("/platforms", { ...editing, name });
        toast.success("Platform added");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}"? Historical sales keep their reference.`)) return;
    try {
      await api.delete(`/platforms/${row.id}`);
      toast.success("Platform deactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="border border-canvas-line bg-canvas-card" data-testid="platforms-panel">
      <div className="px-4 py-3 border-b border-canvas-line flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            10 / External Booking Platforms
          </div>
          <h3 className="font-display text-base font-semibold text-ink mt-1">
            TVS Direct · Flipkart · Amazon · ...
          </h3>
          <p className="text-xs text-ink-muted mt-1">
            Used when a customer pre-pays the vehicle on an external platform — the platform&apos;s payment becomes a receivable owed to you.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="platform-add"
          className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand inline-flex items-center gap-1"
        >
          <Plus size={11} weight="bold" /> Add platform
        </button>
      </div>

      <table className="w-full text-sm" data-testid="platforms-table">
        <thead className="border-b border-canvas-line bg-canvas-panel">
          <tr>
            {["Name", "Code", "Contact", "Phone", "Status", ""].map((h) => (
              <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-8 text-center font-mono text-xs text-ink-subtle">
                No platforms yet.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`platform-row-${r.id}`}
              className="border-b border-canvas-line last:border-b-0"
            >
              <td className="px-3 py-2 font-semibold">{r.name}</td>
              <td className="px-3 py-2 font-mono text-[11px]">{r.code || "—"}</td>
              <td className="px-3 py-2 text-xs">{r.contact_person || "—"}</td>
              <td className="px-3 py-2 font-mono text-[11px]">{r.phone || "—"}</td>
              <td className="px-3 py-2">
                <span
                  className={`inline-flex px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${
                    r.active
                      ? "border-[#00B341] bg-[#00B341]/10 text-[#00713a]"
                      : "border-canvas-line bg-canvas-panel text-ink-subtle"
                  }`}
                >
                  {r.active ? "Active" : "Inactive"}
                </span>
              </td>
              <td className="px-3 py-2 text-right">
                <div className="inline-flex gap-1">
                  <button
                    onClick={() => setEditing(r)}
                    data-testid={`platform-edit-${r.id}`}
                    className="p-1.5 border border-canvas-line hover:border-ink"
                    title="Edit"
                  >
                    <PencilSimple size={12} weight="bold" />
                  </button>
                  {r.active && (
                    <button
                      onClick={() => remove(r)}
                      data-testid={`platform-delete-${r.id}`}
                      className="p-1.5 border border-canvas-line hover:border-destructive hover:text-destructive"
                      title="Deactivate"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <form
            onSubmit={save}
            data-testid="platform-form"
            className="bg-canvas-card border border-ink w-full max-w-md p-5 space-y-3"
          >
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
                {editing.id ? "Edit" : "Add"} platform
              </div>
              <h4 className="font-display text-lg font-semibold mt-1">External booking platform</h4>
            </div>

            {[
              { k: "name", l: "Name *", testId: "platform-form-name" },
              { k: "code", l: "Code (e.g. TVS-DIRECT)", testId: "platform-form-code" },
              { k: "contact_person", l: "Contact person", testId: "platform-form-contact" },
              { k: "phone", l: "Phone", testId: "platform-form-phone" },
              { k: "email", l: "Email", testId: "platform-form-email" },
              { k: "notes", l: "Notes", testId: "platform-form-notes" },
            ].map((f) => (
              <div key={f.k}>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                  {f.l}
                </label>
                <input
                  value={editing[f.k] || ""}
                  onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })}
                  data-testid={f.testId}
                  className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
                />
              </div>
            ))}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-3 py-1.5 border border-canvas-line text-xs hover:border-ink"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="platform-form-save"
                className="px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand"
              >
                {saving ? "Saving…" : editing.id ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
